/**
 * oRPC エラーを Dresscode の ErrorResponse 形式に変換するアダプタ。
 *
 * Backend の createZodValidationErrorInterceptor が ORPCError.data に
 * displayMessages / validationMessages を付与するため、
 * それを ErrorResponse 互換オブジェクトとして取り出す。
 */
import { ORPCError } from '@orpc/client';
import { ErrorCodeSchema, ErrorResponseSchema, type ErrorResponse } from '../error-response-schema';

/**
 * ORPCError.data に Backend が付与する追加フィールド。
 * Backend 側 createZodValidationErrorInterceptor が設定する形式と一致させる。
 */
interface OrpcEnrichedErrorData {
  displayMessage?: string;
  displayMessages?: Record<string, string>;
  validationMessages?: Record<string, string[]>;
  issues?: unknown[];
}
function isOrpcEnrichedData(data: unknown): data is OrpcEnrichedErrorData {
  if (data === null || typeof data !== 'object') {
    return false;
  }
  return 'displayMessage' in data || 'displayMessages' in data || 'validationMessages' in data;
}
export function isOrpcError(error: unknown): error is ORPCError<string, unknown> {
  return error instanceof ORPCError;
}

/**
 * OpenAPILink は Nest の ErrorResponse JSON を `data.body` に格納する。
 * @see @orpc/openapi-client StandardOpenapiLinkCodec.decode
 */
function unwrapOrpcErrorPayload(data: unknown): unknown {
  if (data === null || typeof data !== 'object') {
    return data;
  }
  if ('body' in data) {
    const body = (data as {
      body: unknown;
    }).body;
    if (body !== null && typeof body === 'object' && 'statusCode' in body && typeof (body as {
      statusCode: unknown;
    }).statusCode === 'number') {
      return body;
    }
  }
  return data;
}
function parseErrorResponsePayload(payload: unknown): ErrorResponse | null {
  const parsed = ErrorResponseSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}

/** 全体パースが失敗しても code だけは残す。 */
function salvageErrorCode(payload: unknown): string | undefined {
  return ErrorCodeSchema.safeParse(payload).data?.code;
}

/**
 * ORPCError を ErrorResponse 互換オブジェクトに変換する。
 * Backend が data に displayMessages/validationMessages を付与していない場合は
 * 最低限の statusCode + systemMessage のみ返す。
 */
export function convertOrpcErrorToErrorResponse(error: ORPCError<string, unknown>): ErrorResponse {
  const payload = unwrapOrpcErrorPayload(error.data);
  const fromErrorResponse = parseErrorResponsePayload(payload);
  if (fromErrorResponse != null) {
    return {
      ...fromErrorResponse,
      statusCode: fromErrorResponse.statusCode ?? error.status
    };
  }
  if (isOrpcEnrichedData(payload)) {
    return {
      statusCode: error.status,
      code: salvageErrorCode(payload),
      displayMessage: payload.displayMessage,
      displayMessages: payload.displayMessages ?? {},
      validationMessages: payload.validationMessages,
      systemMessage: error.message
    };
  }
  return {
    statusCode: error.status,
    code: salvageErrorCode(payload),
    displayMessages: {},
    systemMessage: error.message
  };
}